// services/useClients.ts
import { useState } from 'react'
import { useFetchClientsByTenantList } from '@/features/client/services'
import { ClientItem } from '@/types/client'
import { UserType } from '@/types/user'
import { TypeCargoUser } from '@/lib/enums'
import { useFetchClientsByProduct } from '@/features/products/services/useFetchClientsByProduct'
import { useFetchClientsProductsByUsersAndTenant } from '@/features/products/services/useFetchClientsProductsByUsersAndTenant'

type UseClientProps = { user: UserType | null; id_tenant?: string | null }

const onlyDigits = (v: string) => v.replace(/\D/g, '')
const matchClient = (client: ClientItem, searchTerm: string) => {
  const term = (searchTerm ?? '').trim().toLowerCase()
  if (!term) return true
  const razao = (client.razao_social ?? '').toLowerCase()
  const cnpjRaw = String(client.cnpjcpf ?? client.id_client ?? '')
  const ownerName = (client.nomeProprietario ?? client.owner?.nomeProprietario ?? '').toLowerCase()
  const ownerCpfRaw = String(client.cpfProprietario ?? client.owner?.cpfProprietario ?? '')
  const cnpjDigits = onlyDigits(cnpjRaw)
  const ownerCpfDigits = onlyDigits(ownerCpfRaw)
  const termDigits = onlyDigits(term)
  return (
    razao.includes(term) ||
    ownerName.includes(term) ||
    (!!termDigits && (cnpjDigits.includes(termDigits) || ownerCpfDigits.includes(termDigits)))
  )
}
const byRazao = (a: ClientItem, b: ClientItem) => (a.razao_social || '').localeCompare(b.razao_social || '')

const useClients = ({ user, id_tenant }: UseClientProps) => {
  const [searchTerm, setSearchTerm] = useState('')

  const cnpj = user?.typeUser === 'ADMIN' ? (id_tenant ?? undefined) : user?.id_tenant
  const { data: clientsByTenant = [] } = useFetchClientsByTenantList(cnpj || '', { enabled: !!cnpj })

  const { data: clientsByAdmin = [] } = useFetchClientsProductsByUsersAndTenant(cnpj || '', { enabled: !!cnpj })

  const { data: clientsByUser = [] } = useFetchClientsByProduct(user?.id, cnpj || '', { enabled: !!cnpj })

  let base: ClientItem[] = []
  if (user?.typeCargo === TypeCargoUser.CEO || user?.permissions.permission_geral) {
    base = clientsByTenant
  } else if (user?.permissions.permission_product) {
    const allowedIds = new Set(clientsByAdmin.map(p => String(p.id_client)))
    base = clientsByTenant.filter((c: ClientItem) => allowedIds.has(String(c.cnpjcpf ?? c.id_client)))
  } else if (user?.typeUser === 'USUARIO') {
    const userId = String(user?.id)
    base = clientsByTenant.filter(
      (client: ClientItem) => String(client.id_gerente) === userId || String(client.id_responsavel) === userId
    )
  } else {
    // ADMIN não-CEO: restringe aos clientes permitidos pelos produtos do usuário
    const allowedIds = new Set(clientsByUser.map((p: any) => String(p.id_client ?? p.client?.id_client)))
    base = clientsByTenant.filter((c: ClientItem) => allowedIds.has(String(c.cnpjcpf ?? c.id_client)))
  }

  const filteredClients = base.sort(byRazao).filter(c => matchClient(c, searchTerm))
  return { clients: filteredClients, setSearchClient: setSearchTerm }
}

export default useClients
